/**
 * Hooks da aba Varejo (issue #242) — resumo + Curva ABC.
 *
 * Padrão da casa (#75/#206): cache module-level CHAVEADO pelos params (classe
 * do bug #206 — cache único congelava na navegação), TTL 10min e
 * startVisiblePolling (tick skipa em background; volta visível → 1 load).
 * O Σ|CF| do book NÃO vem daqui — a Visão Geral consome useCurvaPnl().books
 * (mesma fonte do card CF por Book #64, paridade por construção).
 */
import { useEffect, useState } from "react";
import {
  getVarejoCurvaAbc,
  getVarejoResumo,
  getVarejoValuation,
  getVarejoValuationViva,
} from "../api/client";
import type {
  CurvaAbcResponse,
  ValuationVarejoResponse,
  VarejoResumoResponse,
} from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry<T> {
  expiresAt: number;
  promise: Promise<T>;
}

const resumoCache = new Map<string, CacheEntry<VarejoResumoResponse>>();
const abcCache = new Map<string, CacheEntry<CurvaAbcResponse>>();

export function invalidateVarejoCache(): void {
  resumoCache.clear();
  abcCache.clear();
}

interface State<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
}

function useCachedFetch<T>(
  cache: Map<string, CacheEntry<T>>,
  key: string,
  fetcher: () => Promise<T>,
): State<T> {
  const [state, setState] = useState<State<T>>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    setState((s) => ({ ...s, isLoading: true }));

    function load() {
      const now = Date.now();
      const hit = cache.get(key);
      if (!hit || hit.expiresAt < now) {
        cache.set(key, { expiresAt: now + REFETCH_MS, promise: fetcher() });
      }
      cache
        .get(key)!
        .promise.then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key); // próxima rodada tenta de novo
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

export function useVarejoResumo(): State<VarejoResumoResponse> {
  return useCachedFetch(resumoCache, "resumo", () => getVarejoResumo());
}

export function useVarejoCurvaAbc(params: {
  book: string;
  de?: string;
  ate?: string;
}): State<CurvaAbcResponse> {
  const key = `${params.book}|${params.de ?? ""}|${params.ate ?? ""}`;
  return useCachedFetch(abcCache, key, () => getVarejoCurvaAbc(params));
}

/**
 * Valuation Varejo (réplica do Estudo MTM #4, PR2) — SNAPSHOT ESTÁTICO.
 *
 * Deliberadamente SEM polling/startVisiblePolling: o valuation é uma foto do
 * Estudo no export, não um feed vivo — refetchar de minuto em minuto só geraria
 * QPS sem número novo. O toggle Nominal/Indexado é puro client-side (off/on já
 * vêm no mesmo payload), então trocar de aba/toggle NÃO refetcha. Carrega 1×
 * no mount; a curva as-of vem do default (hoje) no backend.
 */
export function useVarejoValuation(): State<ValuationVarejoResponse> {
  const [state, setState] = useState<State<ValuationVarejoResponse>>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    getVarejoValuation()
      .then((data) => {
        if (!cancel) setState({ data, isLoading: false, error: null });
      })
      .catch((err: Error) => {
        if (!cancel) setState({ data: null, isLoading: false, error: err });
      });
    return () => {
      cancel = true;
    };
  }, []);

  return state;
}


/**
 * Carteira VIVA (#2, 10/08) — SEM polling (o payload default é snapshot
 * precomputado com as-of próprio; #60). `spread` como dependência: mudar o
 * spread REFETCHA (spread≠0 = recálculo live no backend — D3: prêmio de risco
 * é ato explícito, nunca cache).
 */
export function useVarejoValuationViva(
  spread: number,
): State<ValuationVarejoResponse> {
  const [state, setState] = useState<State<ValuationVarejoResponse>>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    setState((s) => ({ ...s, isLoading: true, error: null }));
    getVarejoValuationViva(spread)
      .then((data) => {
        if (!cancel) setState({ data, isLoading: false, error: null });
      })
      .catch((err: Error) => {
        if (!cancel) setState({ data: null, isLoading: false, error: err });
      });
    return () => {
      cancel = true;
    };
  }, [spread]);

  return state;
}
